import { Timestamp } from "firebase/firestore";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { ability } from "@/components/can/Can";
import { captureException } from "@/services/log";
import defineAbilityFor from "@/src/define-ability-for";
import {
  ID_CADASTROS_EMBAIXADORES,
  ID_CADASTROS_GRUPOS_DE_PERGUNTAS,
  ID_CADASTROS_PERGUNTAS,
  ID_CADASTROS_PROJETOS,
  ID_CADASTROS_QUESTIONARIOS,
  ID_CADASTROS_REGIOES,
  ID_CADASTROS_TIPO_DE_RESPOSTAS,
  ID_COLABORADORES,
  ID_COMITE_DE_INOVACAO,
  ID_CONFIGURACAO_FORMULARIO_IDEIAS,
  ID_CONFIGURACOES_CATEGORIA_DE_INOVACAO,
  ID_CONFIGURACOES_TIPO_DE_JOGADA,
  ID_CONVIDAR_COLABORADORES,
  ID_DESAFIOS_INOVACAO_CATEGORIA_DE_DESAFIOS,
  ID_DESAFIOS_INOVACAO_DESAFIOS,
  ID_DESAFIOS_INOVACAO_REGULAMENTOS,
  ID_EMPRESA,
  ID_EXTRAS_ALTERAR_FASE_DA_EMPRESA,
  ID_EXTRAS_DESCONSIDERAR_AVALIACAO,
  ID_EXTRAS_IMPLEMENTACAO_DAS_JOGADAS,
  ID_EXTRAS_VISUALIZAR_NOTAS_DE_AVALIADORES,
  ID_GERENCIAR_EVIDENCIAS,
  ID_JOGADAS_DE_INOVACAO,
  ID_LISTAGEM_IDEIAS_IDEIAS,
  ID_LISTAGEM_IDEIAS_REGULAMENTOS,
  ID_MARCO,
  ID_QUESTIONARIO_INOVACAO,
  ID_RANKING_COLABORADORES,
  ID_RELATORIOS_DIAGNOSTICO,
  ID_RELATORIOS_VISAO_GERAL_DESAFIOS,
  ID_RELATORIOS_VISAO_GERAL_IDEIAS,
  ID_RESPONDER_QUESTIONARIO,
  ID_RESULTADO_VOTACAO,
  ID_TIPO_DE_INOVACAO,
  ID_TODAS_ATIVIDADES,
  ID_CONFIGURACAO_CONTAS_EMAIL,
  ID_CONFIGURACAO_EVENTOS_EMAIL,
  ID_TRIAGEM_IDEIAS_IA,
  ID_CONFIGURACAO_TEMPLATES_RELATORIOS
} from "@/utils/constants";
import { getEmpresaById } from "../empresas/firestore";
import { addAutorizacao, getAutorizacoes } from "../perfil-de-acesso/firestore";

interface UserState {
  activeLicenciadaId?: string;
  activeEmpresaId?: string;
  activeEmpresa?: TEmpresa;
  activeProjeto?: TProjeto["id"] | null;
  colaborador?: TColaborador;
  autorizacoes?: TAutorizacao[];
  reloadConvites?: boolean;

  reset: () => void;
}

const initialState: Omit<UserState, "reset"> = {
  activeEmpresaId: undefined,
  activeEmpresa: undefined,
  colaborador: undefined,
  autorizacoes: undefined,
  reloadConvites: false
};

const useUserStore = create(
  subscribeWithSelector<UserState>((set) => ({
    ...initialState,
    reset() {
      set(initialState);
    }
  }))
);

useUserStore.subscribe(
  (state) => state.activeEmpresaId,
  function fetchEmpresa(activeEmpresaId) {
    void setActiveEmpresa(activeEmpresaId);
    void setAutorizacoes(activeEmpresaId);
  }
);

useUserStore.subscribe(
  function (state): [TEmpresa | undefined, TColaborador | undefined, TAutorizacao[] | undefined] {
    return [state.activeEmpresa, state.colaborador, state.autorizacoes];
  },
  function updateAbility([activeEmpresa, colaborador, autorizacoes]) {
    if (activeEmpresa && colaborador) {
      const newAbility = defineAbilityFor(activeEmpresa, colaborador, autorizacoes);
      ability.update(newAbility.rules);
    } else {
      ability.update([]);
    }
  }
);
export const getActiveLicenciadaId = (): string | undefined => {
  return useUserStore.getState().activeLicenciadaId;
};
export const setActiveLicenciadaId = (activeLicenciadaId: UserState["activeLicenciadaId"]) =>
  useUserStore.setState(() => ({ activeLicenciadaId }));
export const setActiveEmpresaId = (activeEmpresaId: UserState["activeEmpresaId"]) =>
  useUserStore.setState(() => ({ activeEmpresaId }));
export const setColaborador = (colaborador: UserState["colaborador"]) => useUserStore.setState(() => ({ colaborador }));
export const setReloadConvites = (convite: UserState["reloadConvites"]) =>
  useUserStore.setState(() => ({ reloadConvites: !convite }));
export const setActiveProjeto = (activeProjeto: UserState["activeProjeto"] | null) =>
  useUserStore.setState(() => ({ activeProjeto }));

const setActiveEmpresa = async (activeEmpresaId: UserState["activeEmpresaId"]) => {
  if (activeEmpresaId) {
    const empresaSnapshot = await getEmpresaById(activeEmpresaId).catch((error: Error) => {
      captureException(error, true);
      throw error;
    });
    useUserStore.setState(() => ({ activeEmpresa: empresaSnapshot.exists() ? empresaSnapshot.data() : undefined }));
  } else {
    useUserStore.setState(() => ({ activeEmpresa: undefined }));
  }
};

// Template for all possible permissions
const permissionTemplates = [
  { tipoAutorizacao: ID_EMPRESA, label: "Empresa" },
  { tipoAutorizacao: ID_COLABORADORES, label: "Colaboradores" },
  { tipoAutorizacao: ID_LISTAGEM_IDEIAS_IDEIAS, label: "Listagem de Ideias » Ideias" },
  { tipoAutorizacao: ID_CADASTROS_QUESTIONARIOS, label: "Cadastros » Questionários" },
  { tipoAutorizacao: ID_CADASTROS_GRUPOS_DE_PERGUNTAS, label: "Cadastros » Grupos de Perguntas" },
  { tipoAutorizacao: ID_CADASTROS_PERGUNTAS, label: "Cadastros » Perguntas" },
  { tipoAutorizacao: ID_CADASTROS_TIPO_DE_RESPOSTAS, label: "Cadastros » Tipo de Respostas" },
  { tipoAutorizacao: ID_DESAFIOS_INOVACAO_DESAFIOS, label: "Desafios de Inovação » Desafios" },
  { tipoAutorizacao: ID_MARCO, label: "Marco" },
  { tipoAutorizacao: ID_CONFIGURACOES_CATEGORIA_DE_INOVACAO, label: "Configurações » Categorias de Inovação" },
  { tipoAutorizacao: ID_CONFIGURACOES_TIPO_DE_JOGADA, label: "Configurações » Tipo de jogada" },
  { tipoAutorizacao: ID_CADASTROS_PROJETOS, label: "Cadastros » Projetos" },
  { tipoAutorizacao: ID_DESAFIOS_INOVACAO_REGULAMENTOS, label: "Desafios de Inovação » Regulamentos" },
  { tipoAutorizacao: ID_LISTAGEM_IDEIAS_REGULAMENTOS, label: "Listagem de Ideias » Regulamentos" },
  { tipoAutorizacao: ID_COMITE_DE_INOVACAO, label: "Comitê de inovação" },
  { tipoAutorizacao: ID_TODAS_ATIVIDADES, label: "Todas Atividades" },
  { tipoAutorizacao: ID_RANKING_COLABORADORES, label: "Ranking de colaboradores" },
  { tipoAutorizacao: ID_EXTRAS_IMPLEMENTACAO_DAS_JOGADAS, label: "Extras » Implementação das jogadas" },
  { tipoAutorizacao: ID_TIPO_DE_INOVACAO, label: "Tipo de inovação" },
  { tipoAutorizacao: ID_JOGADAS_DE_INOVACAO, label: "Jogadas de inovação" },
  {
    tipoAutorizacao: ID_DESAFIOS_INOVACAO_CATEGORIA_DE_DESAFIOS,
    label: "Desafios de Inovação » Categorias de Desafios"
  },
  { tipoAutorizacao: ID_RELATORIOS_VISAO_GERAL_IDEIAS, label: "Relatórios » Visão Geral de Ideias" },
  { tipoAutorizacao: ID_RELATORIOS_VISAO_GERAL_DESAFIOS, label: "Relatórios » Visão Geral de Desafios" },
  { tipoAutorizacao: ID_GERENCIAR_EVIDENCIAS, label: "Gerenciar Evidências" },
  { tipoAutorizacao: ID_EXTRAS_VISUALIZAR_NOTAS_DE_AVALIADORES, label: "Extras » Visualizar notas de avaliadores" },
  { tipoAutorizacao: ID_EXTRAS_DESCONSIDERAR_AVALIACAO, label: "Extras » Desconsiderar avaliação" },
  { tipoAutorizacao: ID_CADASTROS_REGIOES, label: "Cadastros » Regiões" },
  { tipoAutorizacao: ID_RESPONDER_QUESTIONARIO, label: "Responder Questionário" },
  { tipoAutorizacao: ID_CONVIDAR_COLABORADORES, label: "Convidar Colaboradores" },
  { tipoAutorizacao: ID_RELATORIOS_DIAGNOSTICO, label: "Relatórios" },
  { tipoAutorizacao: ID_EXTRAS_ALTERAR_FASE_DA_EMPRESA, label: "Extras » Alterar fase da Empresa" },
  { tipoAutorizacao: ID_QUESTIONARIO_INOVACAO, label: "Questionário de Inovação" },
  { tipoAutorizacao: ID_RESULTADO_VOTACAO, label: "Votação Prêmio Empresa Inspiradora" },
  { tipoAutorizacao: ID_CADASTROS_EMBAIXADORES, label: "Cadastros » Embaixadores" },
  { tipoAutorizacao: ID_CONFIGURACAO_FORMULARIO_IDEIAS, label: "Configuração Formulário Ideias" },
  { tipoAutorizacao: ID_CONFIGURACAO_CONTAS_EMAIL, label: "Configuração Contas de Email" },
  { tipoAutorizacao: ID_CONFIGURACAO_EVENTOS_EMAIL, label: "Configuração Eventos de Email" },
  { tipoAutorizacao: ID_TRIAGEM_IDEIAS_IA, label: "Ver aderência IA" },
  { tipoAutorizacao: ID_CONFIGURACAO_TEMPLATES_RELATORIOS, label: "Templates Relatórios" }
];

// Role types
const roleTypes = ["1"]; //, "2", "3", "4", "5"

// Dynamically generate the full list
const lista = roleTypes.flatMap((roleType) =>
  permissionTemplates.map((template) => ({
    ...template,
    leitura: true,
    criacao: true,
    editar: true,
    remover: true,
    tipo: roleType
  }))
);

// prettier-ignore
export const setAutorizacoes = async (activeEmpresaId: UserState["activeEmpresaId"]) => {
  if (activeEmpresaId) {
    const autorizacaoSnapshot = await getAutorizacoes(activeEmpresaId).catch((error: Error) => {
      captureException(error, true);
      throw error;
    });
    const autorizacoes = autorizacaoSnapshot.docs.map((document_) => document_.data());
    if (autorizacoes && autorizacoes.length === 0) {
      // Array de Promises das chamadas addAutorizacao
      const promises = lista.map((item) => {
        return addAutorizacao(activeEmpresaId, {
          ...item,
          updatedAt: Timestamp.now(),
          createdBy: "",
          createdAt: Timestamp.now()
        });
      });

      await Promise.all(promises);

      const newAutorizations = await getAutorizacoes(activeEmpresaId).catch((error: Error) => {
        captureException(error, true);
        throw error;
      });

      useUserStore.setState(() => ({ autorizacoes: newAutorizations.docs.map((document_) => document_.data()) }));
    } else {
      useUserStore.setState(() => ({ autorizacoes: autorizacoes }));
    }
  }
};

export default useUserStore;
